/**
 * Product Hero Gallery Media Hub behavior.
 *
 * Owns the mobile Media Hub modal, section navigation, embedded playback, and
 * gallery-to-lightbox routing without initializing the public gallery runtime.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryMediaHub';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryMediaHubBehavior {
    initializeMediaHubState() {
      this.mediaHubPendingScrollTimer = null;
      this.mediaHubScrollFrame = null;
    }

    destroyMediaHub() {
      if (this.mediaHubPendingScrollTimer) {
        window.clearTimeout(this.mediaHubPendingScrollTimer);
        this.mediaHubPendingScrollTimer = null;
      }

      this.mediaHubPendingScrollTarget = null;
      this.mediaHubSectionObserver?.disconnect?.();
      this.mediaHubSectionObserver = null;
      this.mediaHubVisibleSections?.clear?.();

      if (this.mediaHubScrollFrame !== null && this.mediaHubScrollFrame !== undefined) {
        window.cancelAnimationFrame?.(this.mediaHubScrollFrame);
        this.mediaHubScrollFrame = null;
      }
    }

    initMediaHubModal() {
      this.mediaHubModal = document.querySelector(`#ProductMediaHubModal-${this.sectionId}`);
      if (!this.mediaHubModal) return;

      this.mediaHubCurrentView = null;
      this.mediaHubGalleryIndex = 0;
      this.mediaHubPendingScrollTarget = null;
      this.mediaHubSectionObserver = null;
      this.mediaHubVisibleSections = new Map();
      this.mediaHubBody = this.mediaHubModal.querySelector('.product__media-hub-body');
      this.mediaHubActionButtons = this.mediaHubModal.querySelectorAll('.product__media-hub-header [data-hub-view]');
      this.mediaHubBackBtn = this.mediaHubModal.querySelector('[data-hub-close]');
      this.mediaHubDismissBtn = this.mediaHubModal.querySelector('[data-hub-dismiss]');
      this.mediaHubVideoFrame = this.mediaHubModal.querySelector('[data-hub-video-frame]');
      this.mediaHubVideoNative = this.mediaHubModal.querySelector('[data-hub-video-native]');
      this.mediaHubTourFrame = this.mediaHubModal.querySelector('[data-hub-tour-frame]');
      this.mediaHubFeaturedButton = this.mediaHubModal.querySelector('[data-hub-featured-open]');
      this.mediaHubFeaturedImage = this.mediaHubModal.querySelector('[data-hub-featured-image]');
      this.mediaHubGalleryButtons = this.mediaHubModal.querySelectorAll('[data-hub-gallery-item]');
      this.mediaHubSections = Array.from(this.mediaHubModal.querySelectorAll('[data-hub-section]'));
      this.mediaHubSectionMap = this.mediaHubSections.reduce((acc, section) => {
        acc[section.dataset.hubSection] = section;
        return acc;
      }, {});

      this.initMediaHubSectionObserver();

      if (this.mediaHubBackBtn) {
        this.mediaHubBackBtn.addEventListener('click', () => this.closeMediaHubModal(), {
          signal: this.eventAbortController.signal,
        });
      }

      if (this.mediaHubDismissBtn) {
        this.mediaHubDismissBtn.addEventListener('click', () => this.closeMediaHubModal({ resetView: false }), {
          signal: this.eventAbortController.signal,
        });
      }

      this.mediaHubActionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const { hubView } = button.dataset;
          this.scrollMediaHubToSection(hubView || 'gallery');
        }, { signal: this.eventAbortController.signal });
      });

      this.mediaHubModal.querySelectorAll('[data-hub-gallery-open]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = parseInt(button.dataset.hubGalleryOpen || '0', 10);
          this.updateMediaHubGallery(index);
          this.openLightbox(index, { source: 'hub' });
        }, { signal: this.eventAbortController.signal });
      });

      if (this.mediaHubFeaturedButton) {
        this.mediaHubFeaturedButton.addEventListener('click', () => {
          this.openLightbox(this.mediaHubGalleryIndex, { source: 'hub' });
        }, { signal: this.eventAbortController.signal });
      }

      if (this.mediaHubBody) {
        this.mediaHubBody.addEventListener(
          'scroll',
          () => {
            this.scheduleMediaHubNavSync();
          },
          { passive: true, signal: this.eventAbortController.signal },
        );
      }

      this.mediaHubModal.addEventListener(
        'click',
        (event) => {
          if (event.target === this.mediaHubModal) {
            this.closeMediaHubModal();
          }
        },
        { signal: this.eventAbortController.signal },
      );

      this.handleMediaHubKeydown = (event) => {
        if (event.key === 'Escape' && this.mediaHubModal?.classList.contains('is-open')) {
          this.closeMediaHubModal();
        }
      };
      document.addEventListener('keydown', this.handleMediaHubKeydown, {
        signal: this.eventAbortController.signal,
      });
    }

    openMediaHubModal({ view = null, galleryIndex = null, restoreFocusEl = null, pushHistory = true, focus = true } = {}) {
      if (!this.mediaHubModal) return;
      this.mediaHubRestoreFocusEl = restoreFocusEl || document.activeElement || this.mobileMediaHubBtn || null;
      const nextView = view || this.mediaHubCurrentView || this.getDefaultMediaHubSection();

      this.updateMediaHubGallery(
        typeof galleryIndex === 'number' ? galleryIndex : this.mediaHubGalleryIndex,
      );

      this.setModalState(this.mediaHubModal, true);
      this.syncMediaHubEmbeds(nextView);
      this.scrollMediaHubToSection(nextView, { behavior: 'auto', focusButton: false, syncHistory: false });

      if (pushHistory) {
        const currentPayload = this.getHistoryStatePayload();
        const { replace } = this.prepareHistoryForOverlay({
          allowReplaceCurrent: currentPayload?.overlay === 'modal',
        });
        this.setHistoryState(
          {
            overlay: 'modal',
            modalType: 'hub',
            view: nextView,
            galleryIndex: this.mediaHubGalleryIndex,
          },
          { replace },
        );
      }

      if (focus && this.mediaHubBackBtn) this.mediaHubBackBtn.focus();
    }

    closeMediaHubModal({ restoreFocus = true, resetView = true, syncHistory = true } = {}) {
      if (!this.mediaHubModal) return;

      if (syncHistory) {
        this.navigateBackOrClose(
          'modal',
          () => this.closeMediaHubModal({ restoreFocus, resetView, syncHistory: false }),
        );
        return;
      }

      window.clearTimeout(this.mediaHubPendingScrollTimer);
      this.mediaHubPendingScrollTimer = null;
      this.mediaHubPendingScrollTarget = null;
      this.setModalState(this.mediaHubModal, false);
      this.stopMediaHubPlayback();
      if (resetView && this.mediaHubBody) {
        this.mediaHubBody.scrollTo({ top: 0, behavior: 'auto' });
        this.updateMediaHubNavState(this.getDefaultMediaHubSection());
      }
      this.syncCurrentHistoryEntryToUi();
      if (restoreFocus) {
        const focusTarget = this.mediaHubRestoreFocusEl || this.mobileMediaHubBtn;
        focusTarget?.focus?.();
      }
    }

    scheduleMediaHubNavSync() {
      if (this.mediaHubScrollFrame !== null) return;

      this.mediaHubScrollFrame = window.requestAnimationFrame(() => {
        this.mediaHubScrollFrame = null;
        if (!this.isDestroyed) this.syncMediaHubNavToScroll();
      });
    }

    stopMediaHubPlayback() {
      if (this.mediaHubVideoFrame) {
        this.pauseEmbeddedFrame(this.mediaHubVideoFrame);
        this.mediaHubVideoFrame.hidden = true;
      }
      if (this.mediaHubVideoNative) {
        this.mediaHubVideoNative.pause();
        this.mediaHubVideoNative.removeAttribute('src');
        this.mediaHubVideoNative.load();
        this.mediaHubVideoNative.hidden = true;
      }
      if (this.mediaHubTourFrame) {
        this.pauseEmbeddedFrame(this.mediaHubTourFrame);
        this.mediaHubTourFrame.hidden = true;
      }
    }

    getDefaultMediaHubSection() {
      if (this.mediaHubSectionMap?.tour) return 'tour';
      if (this.mediaHubSectionMap?.video) return 'video';
      if (this.mediaHubSectionMap?.gallery) return 'gallery';
      return null;
    }

    syncMediaHubEmbeds(activeView = this.mediaHubCurrentView || this.getDefaultMediaHubSection()) {
      const showVideo = activeView === 'video';
      const showTour = activeView === 'tour';
      const embedUrl = this.videoModalUrl ? this.getVideoEmbedUrl(this.videoModalUrl) : null;

      if (this.mediaHubVideoFrame) {
        if (showVideo && embedUrl) {
          this.setFrameSource(this.mediaHubVideoFrame, embedUrl);
          this.mediaHubVideoFrame.hidden = false;
        } else {
          this.pauseEmbeddedFrame(this.mediaHubVideoFrame);
          this.mediaHubVideoFrame.hidden = true;
        }
      }

      if (this.mediaHubVideoNative) {
        if (showVideo && this.videoModalUrl && !embedUrl) {
          if (this.mediaHubVideoNative.getAttribute('src') !== this.videoModalUrl) {
            this.mediaHubVideoNative.src = this.videoModalUrl;
          }
          this.mediaHubVideoNative.hidden = false;
        } else {
          this.mediaHubVideoNative.pause();
          this.mediaHubVideoNative.hidden = true;
        }
      }

      if (this.mediaHubTourFrame) {
        if (showTour && this.tourModalUrl) {
          this.setFrameSource(this.mediaHubTourFrame, this.tourModalUrl);
          this.mediaHubTourFrame.hidden = false;
        } else {
          this.pauseEmbeddedFrame(this.mediaHubTourFrame);
          this.mediaHubTourFrame.hidden = true;
        }
      }
    }

    initMediaHubSectionObserver() {
      if (!this.mediaHubBody || !this.mediaHubSections.length || !('IntersectionObserver' in window)) return;

      this.mediaHubSectionObserver?.disconnect?.();
      this.mediaHubVisibleSections.clear();

      this.mediaHubSectionObserver = new IntersectionObserver(
        (entries) => {
          if (this.isDestroyed) return;

          entries.forEach((entry) => {
            const sectionName = entry.target?.dataset?.hubSection;
            if (!sectionName) return;

            if (entry.isIntersecting) {
              this.mediaHubVisibleSections.set(sectionName, {
                ratio: entry.intersectionRatio,
                top: entry.boundingClientRect.top,
              });
            } else {
              this.mediaHubVisibleSections.delete(sectionName);
            }
          });

          if (!this.mediaHubPendingScrollTarget) {
            this.syncMediaHubNavFromObserver();
          }
        },
        {
          root: this.mediaHubBody,
          threshold: [0, 0.2, 0.35, 0.5, 0.7, 0.9],
          rootMargin: '-8% 0px -42% 0px',
        },
      );

      this.mediaHubSections.forEach((section) => {
        this.mediaHubSectionObserver.observe(section);
      });
    }

    updateMediaHubNavState(view) {
      this.mediaHubCurrentView = view || this.getDefaultMediaHubSection();
      if (this.mediaHubModal?.classList.contains('is-open')) {
        this.syncMediaHubEmbeds(this.mediaHubCurrentView);
      }
      this.mediaHubActionButtons.forEach((button) => {
        const active = button.dataset.hubView === this.mediaHubCurrentView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    syncMediaHubNavFromObserver() {
      if (!this.mediaHubVisibleSections.size) return;

      let bestView = null;
      let bestScore = Number.NEGATIVE_INFINITY;

      this.mediaHubVisibleSections.forEach((metrics, sectionName) => {
        const ratioScore = metrics.ratio * 1000;
        const topScore = -Math.abs(metrics.top);
        const score = ratioScore + topScore;

        if (score > bestScore) {
          bestScore = score;
          bestView = sectionName;
        }
      });

      if (bestView && bestView !== this.mediaHubCurrentView) {
        this.updateMediaHubNavState(bestView);
      }
    }

    getMediaHubSectionForScrollPosition(scrollTop = this.mediaHubBody?.scrollTop || 0) {
      if (!this.mediaHubSections?.length) return null;

      const anchor = scrollTop + Math.min(140, (this.mediaHubBody?.clientHeight || 0) * 0.2 + 48);
      let activeSection = this.mediaHubSections[0];

      this.mediaHubSections.forEach((section, index) => {
        const nextSection = this.mediaHubSections[index + 1];
        const sectionTop = section.offsetTop;
        const nextTop = nextSection ? nextSection.offsetTop : Number.POSITIVE_INFINITY;

        if (anchor >= sectionTop && anchor < nextTop) {
          activeSection = section;
        }
      });

      return activeSection?.dataset?.hubSection || null;
    }

    getMediaHubScrollTopForSection(section) {
      if (!section || !this.mediaHubBody) return 0;

      const sectionHeader = section.querySelector('.product__media-hub-view-header');
      const targetElement = sectionHeader || section;
      const bodyStyles = window.getComputedStyle(this.mediaHubBody);
      const bodyPaddingTop = parseFloat(bodyStyles.paddingTop || '0') || 0;
      const bodyRect = this.mediaHubBody.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const targetTop = this.mediaHubBody.scrollTop + (targetRect.top - bodyRect.top) - bodyPaddingTop;

      return Math.max(0, targetTop);
    }

    scrollMediaHubToSection(view, { behavior = 'smooth', focusButton = true } = {}) {
      if (!this.mediaHubBody) return;
      const nextView = view || this.getDefaultMediaHubSection();
      const targetSection = this.mediaHubSectionMap?.[nextView];
      if (!targetSection) return;

      this.mediaHubPendingScrollTarget = nextView;
      window.clearTimeout(this.mediaHubPendingScrollTimer);
      this.mediaHubPendingScrollTimer = window.setTimeout(() => {
        this.mediaHubPendingScrollTimer = null;
        if (this.isDestroyed) return;
        this.mediaHubPendingScrollTarget = null;
        this.syncMediaHubNavToScroll();
      }, behavior === 'smooth' ? 1200 : 120);
      this.updateMediaHubNavState(nextView);
      const top = this.getMediaHubScrollTopForSection(targetSection);
      this.mediaHubBody.scrollTo({ top, behavior });
      this.scheduleMediaHubNavSync();

      if (focusButton) {
        const activeButton = this.mediaHubModal.querySelector(`.product__media-hub-header [data-hub-view="${nextView}"]`);
        activeButton?.focus();
      }
    }

    syncMediaHubNavToScroll() {
      if (!this.mediaHubBody || !this.mediaHubSections.length) return;

      if (this.mediaHubSectionObserver) {
        if (!this.mediaHubPendingScrollTarget) {
          this.syncMediaHubNavFromObserver();
        }
        return;
      }

      const scrollTop = this.mediaHubBody.scrollTop;
      const pendingView = this.mediaHubPendingScrollTarget;
      if (pendingView) {
        const pendingSection = this.mediaHubSectionMap?.[pendingView];
        if (pendingSection) {
          const targetTop = this.getMediaHubScrollTopForSection(pendingSection);
          if (Math.abs(scrollTop - targetTop) > 24) {
            return;
          }
        }
        window.clearTimeout(this.mediaHubPendingScrollTimer);
        this.mediaHubPendingScrollTimer = null;
        this.mediaHubPendingScrollTarget = null;
      }

      const activeView = this.getMediaHubSectionForScrollPosition(scrollTop);

      if (activeView && activeView !== this.mediaHubCurrentView) {
        this.updateMediaHubNavState(activeView);
      }
    }

    updateMediaHubGallery(index = 0) {
      if (!this.mediaHubGalleryButtons.length) return;
      if (!this.imagesData.length) {
        this.parseImagesData();
      }

      const total = this.mediaHubGalleryButtons.length;
      if (!total) return;

      const normalizedIndex = Math.min(Math.max(index, 0), total - 1);
      this.mediaHubGalleryIndex = normalizedIndex;

      const activeImage = this.imagesData[normalizedIndex];
      if (activeImage && this.mediaHubFeaturedImage) {
        this.mediaHubFeaturedImage.sizes = '100vw';
        this.mediaHubFeaturedImage.srcset = activeImage.srcset || '';
        this.mediaHubFeaturedImage.src = activeImage.src;
        this.mediaHubFeaturedImage.alt = activeImage.alt || '';
        this.mediaHubFeaturedImage.hidden = false;
      }

      this.mediaHubGalleryButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === normalizedIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(ProductHeroGalleryMediaHubBehavior.prototype);
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError('Product Hero Gallery Media Hub requires a class constructor.');
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
